import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "sigpad" }
const _hoisted_2 = { class: "sigpad__frame" }
const _hoisted_3 = {
  key: 0,
  class: "sigpad__guide",
  "aria-hidden": "true"
}
const _hoisted_4 = { class: "sigpad__actions" }
const _hoisted_5 = ["disabled"]

import { onBeforeUnmount, onMounted, ref, watch } from 'vue'


export default /*@__PURE__*/_defineComponent({
  __name: 'SignaturePad',
  props: {
    modelValue: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const canvasEl = ref<HTMLCanvasElement | null>(null)
const hasInk = ref(false)
let ctx: CanvasRenderingContext2D | null = null
let drawing = false
let lastX = 0
let lastY = 0
let dpr = 1
let ro: ResizeObserver | null = null

function setupCanvas() {
  const canvas = canvasEl.value
  if (!canvas) return
  const rect = canvas.getBoundingClientRect()
  if (rect.width === 0) return
  dpr = window.devicePixelRatio || 1
  canvas.width = Math.round(rect.width * dpr)
  canvas.height = Math.round(rect.height * dpr)
  ctx = canvas.getContext('2d')
  if (!ctx) return
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = '#142c47'
  ctx.lineWidth = 2.4 * dpr
  /* 크기 재설정으로 지워졌으니 저장된 서명이 있으면 다시 그린다 */
  if (props.modelValue) redrawFromDataUrl(props.modelValue)
}

function redrawFromDataUrl(dataUrl: string) {
  const canvas = canvasEl.value
  if (!canvas || !ctx) return
  const img = new Image()
  img.onload = () => {
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    const scale = Math.min(canvas.width / img.width, canvas.height / img.height) * 0.9
    const w = img.width * scale
    const h = img.height * scale
    ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h)
    hasInk.value = true
  }
  img.src = dataUrl
}

function pointFrom(e: PointerEvent) {
  const canvas = canvasEl.value
  if (!canvas) return { x: 0, y: 0 }
  const rect = canvas.getBoundingClientRect()
  return { x: (e.clientX - rect.left) * dpr, y: (e.clientY - rect.top) * dpr }
}

function onDown(e: PointerEvent) {
  if (!ctx) return
  drawing = true
  try {
    ;(e.target as Element).setPointerCapture?.(e.pointerId)
  } catch {
    /* 활성 포인터가 아니면 캡처 없이 진행 */
  }
  const p = pointFrom(e)
  lastX = p.x
  lastY = p.y
  /* 점만 찍어도 찍히게 */
  ctx.beginPath()
  ctx.moveTo(p.x, p.y)
  ctx.lineTo(p.x + 0.1, p.y + 0.1)
  ctx.stroke()
  hasInk.value = true
  e.preventDefault()
}

function onMove(e: PointerEvent) {
  if (!drawing || !ctx) return
  const p = pointFrom(e)
  ctx.beginPath()
  ctx.moveTo(lastX, lastY)
  ctx.lineTo(p.x, p.y)
  ctx.stroke()
  lastX = p.x
  lastY = p.y
  e.preventDefault()
}

function onUp() {
  if (!drawing) return
  drawing = false
  exportSignature()
}

/* 그린 영역만 잘라 여백 없는 PNG dataURL로 저장한다 */
function exportSignature() {
  const canvas = canvasEl.value
  if (!canvas || !ctx) return
  const { width, height } = canvas
  const data = ctx.getImageData(0, 0, width, height).data
  let minX = width
  let minY = height
  let maxX = -1
  let maxY = -1
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 0) {
        if (x < minX) minX = x
        if (x > maxX) maxX = x
        if (y < minY) minY = y
        if (y > maxY) maxY = y
      }
    }
  }
  if (maxX < 0) {
    emit('update:modelValue', '')
    return
  }
  const pad = Math.round(6 * dpr)
  minX = Math.max(0, minX - pad)
  minY = Math.max(0, minY - pad)
  maxX = Math.min(width - 1, maxX + pad)
  maxY = Math.min(height - 1, maxY + pad)
  const w = maxX - minX + 1
  const h = maxY - minY + 1
  const out = document.createElement('canvas')
  out.width = w
  out.height = h
  const octx = out.getContext('2d')
  if (!octx) return
  octx.drawImage(canvas, minX, minY, w, h, 0, 0, w, h)
  emit('update:modelValue', out.toDataURL('image/png'))
}

function clear() {
  const canvas = canvasEl.value
  if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
  hasInk.value = false
  emit('update:modelValue', '')
}

/* 바깥에서 값이 비워지면 캔버스도 비운다 */
watch(
  () => props.modelValue,
  (v) => {
    if (!v && hasInk.value) {
      const canvas = canvasEl.value
      if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
      hasInk.value = false
    }
  },
)

onMounted(() => {
  setupCanvas()
  const canvas = canvasEl.value
  if (canvas && typeof ResizeObserver !== 'undefined') {
    let first = true
    ro = new ResizeObserver(() => {
      /* 최초 관찰 콜백에서는 재설정하지 않는다(그림이 지워지는 것 방지) */
      if (first) {
        first = false
        return
      }
      setupCanvas()
    })
    ro.observe(canvas)
  }
})

onBeforeUnmount(() => {
  ro?.disconnect()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("canvas", {
        ref_key: "canvasEl",
        ref: canvasEl,
        class: "sigpad__canvas",
        onPointerdown: onDown,
        onPointermove: onMove,
        onPointerup: onUp,
        onPointerleave: onUp,
        onPointercancel: onUp
      }, null, 544),
      (!hasInk.value && !__props.modelValue)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[0] || (_cache[0] = [
            _createElementVNode("span", { class: "sigpad__hint" }, "이 안에 서명하세요", -1)
          ]))]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createElementVNode("button", {
        type: "button",
        class: "sigpad__clear",
        disabled: !hasInk.value && !__props.modelValue,
        onClick: clear
      }, " 지우기 ", 8, _hoisted_5)
    ])
  ]))
}
}

})